/**
 * The application's route table.
 *
 * Split out of App.tsx, which was 411 lines of imports, a six-deep provider
 * stack and this tree all in one component. Route definitions change far more
 * often than the provider stack does, and mixing them meant every new page
 * touched the same file as every provider change.
 *
 * Route ELEMENTS stay declared here rather than in the page modules so the
 * guard/layout/boundary wrapping for every route is visible in one place.
 */
import { Navigate, Route, Routes } from 'react-router-dom';

import { ErrorBoundary } from '@/components/core/common/boundaries/ErrorBoundary';
import { PageBoundary } from '@/components/core/common/boundaries/PageBoundary';
import { ProtectedRoute } from '@/components/core/guards/ProtectedRoute';
import { PublicRoute } from '@/components/core/guards/PublicRoute';
import { AppLayout } from '@/components/core/layout/AppLayout';
import { AccountRestorePage } from '@/pages/account/AccountRestorePage';
import { AuthCallbackPage } from '@/pages/auth/AuthCallbackPage';
import { AuthPage } from '@/pages/auth/AuthPage';
import { DevErrorsPage } from '@/pages/dev/DevErrorsPage';
import { HomePage } from '@/pages/HomePage';
import { KnowledgeArchivePage } from '@/pages/mindspace/KnowledgeArchivePage';
import { LessonLearningPage } from '@/pages/learning/LessonLearningPage';
import { MindspacePage } from '@/pages/mindspace/MindspacePage';
import { MyLearningPage } from '@/pages/learning/MyLearningPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { OnboardingPage } from '@/pages/account/OnboardingPage';
import { PracticeArenaPage } from '@/pages/mindspace/PracticeArenaPage';
import { ProgressPage } from '@/pages/mindspace/ProgressPage';
import { QuickBytesPage } from '@/pages/mindspace/QuickBytesPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import RichRenderHarness from '@/pages/dev/RichRenderHarness';
import { SettingsPage } from '@/pages/account/SettingsPage';
import { BreatheRoomPage } from '@/pages/wellbeing/BreatheRoomPage';
import { FocusRoomPage } from '@/pages/wellbeing/FocusRoomPage';
import { WellbeingPage } from '@/pages/wellbeing/WellbeingPage';
import { WorryRoomPage } from '@/pages/wellbeing/WorryRoomPage';

export function AppRoutes() {
  return (
    <Routes>
      {/* Dev-only rich-content render harness (no auth) -
        used by the XON-96 Playwright rendering check. Gated to
        dev so it never ships in production. */}
      {import.meta.env.DEV && (
        <Route path='/dev/rich-render' element={<RichRenderHarness />} />
      )}

      {/* Home route - main learning interface */}
      <Route
        path='/'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <HomePage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* First-time onboarding wizard. Protected (needs
        auth) but intentionally NOT wrapped in AppLayout -
        it's full-screen with no navbar, and excluded from
        the OnboardingGate to avoid a redirect loop. */}
      <Route
        path='/onboarding'
        element={
          <ProtectedRoute>
            <ErrorBoundary>
              <OnboardingPage />
            </ErrorBoundary>
          </ProtectedRoute>
        }
      />

      {/* Public routes (auth pages). Wrapped in a plain
        ErrorBoundary (not PageBoundary) because there's
        no AppLayout chrome to preserve - a crash here
        should just render the page-variant fallback. */}
      <Route
        path='/auth'
        element={
          <PublicRoute>
            <ErrorBoundary>
              <AuthPage />
            </ErrorBoundary>
          </PublicRoute>
        }
      />

      {/* Legacy auth routes - redirect to new structure */}
      <Route
        path='/auth/login'
        element={<Navigate to='/auth?mode=login' replace />}
      />
      <Route
        path='/auth/register'
        element={<Navigate to='/auth?mode=register' replace />}
      />
      {/* Password-recovery landing. Mounted BARE (like
        /auth/callback, NOT under PublicRoute) so the temporary
        recovery session doesn't bounce the user to home before
        they can set a new password. It preserves the URL token
        that a <Navigate> redirect would otherwise discard. */}
      <Route
        path='/auth/reset-password'
        element={
          <ErrorBoundary>
            <ResetPasswordPage />
          </ErrorBoundary>
        }
      />

      {/* Auth callback route */}
      <Route
        path='/auth/callback'
        element={
          <ErrorBoundary>
            <AuthCallbackPage />
          </ErrorBoundary>
        }
      />

      {/* Account-restore landing page (consumes JWT from email) */}
      <Route
        path='/account/restore'
        element={
          <ErrorBoundary>
            <AccountRestorePage />
          </ErrorBoundary>
        }
      />

      {/* Legacy lessons route - redirect to home */}
      <Route path='/lessons' element={<Navigate to='/' replace />} />

      {/* Legacy route redirects */}
      <Route path='/ongoing' element={<Navigate to='/my-learning' replace />} />
      <Route
        path='/now-learning'
        element={<Navigate to='/my-learning' replace />}
      />
      <Route
        path='/memory-vault'
        element={<Navigate to='/mindspace' replace />}
      />
      {/* Protected routes with AppLayout */}
      <Route
        path='/my-learning'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <MyLearningPage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/my-learning/:id'
        element={
          <ProtectedRoute>
            <AppLayout hideNavbarOnPhone>
              <PageBoundary>
                <LessonLearningPage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/mindspace'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <MindspacePage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Mindspace sub-pages */}
      <Route
        path='/mindspace/knowledge-archive'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <KnowledgeArchivePage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/mindspace/practice-arena'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <PracticeArenaPage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/mindspace/quick-bytes'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <QuickBytesPage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Legacy mindspace route redirects */}
      <Route
        path='/mindspace/concepts'
        element={<Navigate to='/mindspace/knowledge-archive' replace />}
      />
      <Route
        path='/mindspace/revision'
        element={<Navigate to='/mindspace/practice-arena' replace />}
      />
      <Route
        path='/progress'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <ProgressPage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/wellbeing'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <WellbeingPage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/wellbeing/focus'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <FocusRoomPage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/wellbeing/breathe'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <BreatheRoomPage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path='/wellbeing/worry'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <WorryRoomPage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path='/settings'
        element={
          <ProtectedRoute>
            <AppLayout>
              <PageBoundary>
                <SettingsPage />
              </PageBoundary>
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Dev-only error/toast test harness. The route is
        omitted entirely in production builds - visiting
        `/dev/errors` then falls through to the 404 page. */}
      {import.meta.env.DEV && (
        <Route
          path='/dev/errors'
          element={
            <ProtectedRoute>
              <AppLayout>
                <PageBoundary>
                  <DevErrorsPage />
                </PageBoundary>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      )}

      {/* 404 page for unmatched routes */}
      <Route path='*' element={<NotFoundPage />} />
    </Routes>
  );
}
